import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { api } from "../../services/api";

// ─── Types (mirrors the backend's { life_guidance, astro_chart_view } contract) ───

interface WeddingGuidance {
  note: string;
  vastu_direction: string;
  lucky_colors: string[];
  colors_to_avoid: string[];
  recommended_gifts: string[];
  checklist_highlights: string[];
}

interface LifeGuidance {
  healing: string;
  growth_path: string;
  family_cultural_harmony: string;
  wedding_guidance: WeddingGuidance;
}

interface PartnerChartView {
  name: string;
  is_limited: boolean;
  place_provided: boolean;
  place_resolved: boolean;
  lagna: string | null;
  moon_sign: string | null;
  nakshatra: string | null;
  dasha: string | null;
  sub_dasha: string | null;
}

interface Compatibility {
  guna_score: number;
  guna_score_max: number;
  guna_score_percent: number;
  guna_label: string;
  dosha: string[];
  manglik_status: string;
  rashi_relationship: string | null;
}

interface AstroChartView {
  is_limited: boolean;
  partner_a: PartnerChartView;
  partner_b: PartnerChartView;
  compatibility: Compatibility;
  planetary_highlights: string[];
  house_influences: string[];
  dasha_summary: string;
  summary_points: string[];
}

interface MarriageVerdictContent {
  life_guidance: LifeGuidance;
  astro_chart_view: AstroChartView;
}

interface PartnerFormSnapshot {
  full_name: string;
  date_of_birth: string;
  time_of_birth: string;
  place_of_birth: string;
}

interface VerdictResponse {
  success: boolean;
  verdictId: string;
  verdict: MarriageVerdictContent;
  selectedTab: "life_guidance" | "astro_chart_view";
  formInput?: { partner_a: PartnerFormSnapshot; partner_b: PartnerFormSnapshot };
}

type TabKey = "life_guidance" | "astro_chart_view";

// ─── Animation variants ───────────────────────────────────────────────────────

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.07,
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
    },
  }),
};

// ─── Shared UI atoms ──────────────────────────────────────────────────────────

const SectionCard: React.FC<{
  title: string;
  subtitle?: string;
  accent?: string;
  index?: number;
  children: React.ReactNode;
}> = ({ title, subtitle, accent = "#C084FC", index = 0, children }) => (
  <motion.div
    custom={index}
    variants={fadeUp}
    initial="hidden"
    animate="visible"
    className="bg-white border border-[#EEF2FF] rounded-3xl p-5 sm:p-6 mb-4 shadow-sm"
  >
    <div className="flex items-center gap-2 mb-1">
      <span
        className="w-1.5 h-1.5 rounded-full flex-shrink-0"
        style={{ background: accent }}
      />
      <h3
        className="text-[10px] font-semibold tracking-[0.2em] uppercase"
        style={{ color: accent }}
      >
        {title}
      </h3>
    </div>
    {subtitle && (
      <p className="text-[11px] mb-4 ml-3.5" style={{ color: "#9D8FA8" }}>
        {subtitle}
      </p>
    )}
    <div className={subtitle ? "" : "mt-3"}>{children}</div>
  </motion.div>
);

// Circular score gauge
const ScoreGauge: React.FC<{
  score: number;
  max?: number;
  label: string;
  size?: number;
  strokeColor?: string;
}> = ({ score, max = 100, label, size = 80, strokeColor = "#C084FC" }) => {
  const r = (size - 12) / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.min(score / max, 1);
  const offset = circ * (1 - pct);

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#F0EBF5"
          strokeWidth={6}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={strokeColor}
          strokeWidth={6}
          strokeDasharray={circ}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: "stroke-dashoffset 1.2s ease" }}
        />
        <text
          x="50%"
          y="50%"
          dominantBaseline="middle"
          textAnchor="middle"
          fill="#6A548A"
          fontSize={size > 72 ? 16 : 13}
          fontWeight="400"
        >
          {score}
        </text>
        {max !== 100 && (
          <text
            x="50%"
            y="65%"
            dominantBaseline="middle"
            textAnchor="middle"
            fill="#9D8FA8"
            fontSize={8}
          >
            /{max}
          </text>
        )}
      </svg>
      <p className="text-[10px] text-[#9D8FA8] mt-1 text-center leading-tight">
        {label}
      </p>
    </div>
  );
};

const TagBadge: React.FC<{
  text: string;
  color?: "purple" | "blue" | "rose" | "green" | "amber";
}> = ({ text, color = "amber" }) => {
  const styles: Record<string, string> = {
    purple: "bg-[#F7F2FB] text-[#6D4C92] border-[#C084FC]/30",
    blue: "bg-[#F0F9FF] text-[#0369A1] border-[#7DD3FC]/30",
    rose: "bg-[#FFF1F2] text-[#BE123C] border-[#FDA4AF]/30",
    green: "bg-[#F0FDF4] text-[#166534] border-[#86EFAC]/30",
    amber: "bg-[#F7F2FB] text-[#6D4C92] border-[#C084FC]/30",
  };
  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] font-semibold border tracking-wide ${styles[color]}`}
    >
      {text}
    </span>
  );
};

const BulletList: React.FC<{ items: string[]; accent?: string }> = ({
  items,
  accent = "#C084FC",
}) => (
  <ul className="space-y-2">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2 text-xs text-[#5D544D]">
        <span
          className="mt-0.5 flex-shrink-0 w-3 h-3 rounded-full flex items-center justify-center"
          style={{ background: `${accent}22` }}
        >
          <span
            className="w-1 h-1 rounded-full"
            style={{ background: accent }}
          />
        </span>
        {item}
      </li>
    ))}
  </ul>
);

const ColorChip: React.FC<{ color: string }> = ({ color }) => {
  const map: Record<string, string> = {
    Red: "#ef4444",
    Gold: "#f59e0b",
    Cream: "#fef3c7",
    Pink: "#f472b6",
    Orange: "#f97316",
    White: "#f9fafb",
    Green: "#22c55e",
    Blue: "#3b82f6",
    Purple: "#a855f7",
    Yellow: "#eab308",
    Black: "#374151",
    Grey: "#9ca3af",
    Saffron: "#C084FC",
    Maroon: "#9B1C1C",
  };
  return (
    <div
      className="flex items-center gap-2 px-3 py-1.5 rounded-full border"
      style={{ background: "#F7F2FB", borderColor: "#EEF2FF" }}
    >
      <div
        className="w-3 h-3 rounded-full border border-white shadow-sm"
        style={{ background: map[color] || "#ccc" }}
      />
      <span className="text-[11px] font-medium" style={{ color: "#5D544D" }}>
        {color}
      </span>
    </div>
  );
};

// Two-pill tab switcher — matches the PillOption look used in the form step.
const TabToggle: React.FC<{ active: TabKey; onChange: (tab: TabKey) => void }> = ({
  active,
  onChange,
}) => (
  <div className="flex justify-center gap-2 mb-6 flex-wrap">
    {(
      [
        { key: "life_guidance" as const, label: "Life Guidance" },
        { key: "astro_chart_view" as const, label: "Astrological Chart View" },
      ]
    ).map((tab) => (
      <button
        key={tab.key}
        type="button"
        onClick={() => onChange(tab.key)}
        className="px-5 py-2 rounded-full text-xs font-semibold tracking-wide border transition-all duration-200"
        style={
          active === tab.key
            ? {
                background: "linear-gradient(135deg,#C084FC,#6D4C92)",
                borderColor: "#C084FC",
                color: "white",
                boxShadow: "0 2px 6px #C084FC33",
              }
            : { background: "white", borderColor: "#E5E7EB", color: "#6A548A" }
        }
      >
        {tab.label}
      </button>
    ))}
  </div>
);

// Shown in place of Lagna/houses when a partner is missing birth time/place.
const LimitedBanner: React.FC = () => (
  <div
    className="rounded-2xl p-3 mb-4 text-center"
    style={{ background: "#FFF7ED", border: "1px solid #FDBA7433" }}
  >
    <p className="text-[11px] font-medium" style={{ color: "#C2703D" }}>
      For full astrological analysis, please add time and place.
    </p>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const MarriageVerdictResult: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<VerdictResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>("life_guidance");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const raw = sessionStorage.getItem("marriageVerdict");
    if (!raw) {
      setError("No verdict data found.");
      return;
    }
    try {
      const parsed: VerdictResponse = JSON.parse(raw);
      setData(parsed);
      setActiveTab(parsed.selectedTab || "life_guidance");
    } catch {
      setError("Failed to load verdict.");
    }
  }, []);

  // Instant tab switch (no refetch) — persists the choice in the background
  // so a reload restores the same tab; failures are non-blocking.
  const handleTabChange = (tab: TabKey) => {
    setActiveTab(tab);
    if (data?.verdictId) {
      api
        .patch(`/backend/marriage-verdict/${data.verdictId}/tab`, { tab })
        .catch(() => {});
    }
  };

  // Non-destructive — sessionStorage still has formInput + verdictId, so the
  // form reopens pre-filled and resubmits to the same record. The editIntent
  // flag tells the form to skip its "existing verdict → redirect to result"
  // check, since here the user explicitly wants the form, not the result.
  const handleEditDetails = () =>
    navigate("/marriage-verdict", { state: { editIntent: true } });

  // Already persisted server-side on generate/update — just confirm visually.
  const handleSaveVerdict = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleNewVerdict = () => {
    sessionStorage.removeItem("marriageVerdict");
    navigate("/marriage-verdict");
  };

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ background: "linear-gradient(160deg,#F7F2FB,#F7F2FB)" }}
      >
        <div className="text-center">
          <p className="text-red-500 text-sm mb-4">{error}</p>
          <button
            onClick={() => navigate("/marriage-verdict")}
            className="px-6 py-2.5 rounded-full bg-gradient-to-r from-[#C084FC] to-[#7DD3FC] text-white text-xs font-semibold"
          >
            Generate Verdict
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div
          className="w-8 h-8 rounded-full border-2 animate-spin"
          style={{ borderColor: "#C084FC33", borderTopColor: "#C084FC" }}
        />
      </div>
    );
  }

  const lg = data.verdict.life_guidance;
  const astro = data.verdict.astro_chart_view;
  const partnerAInfo = data.formInput?.partner_a;
  const partnerBInfo = data.formInput?.partner_b;

  return (
    <div className="min-h-screen w-full bg-white">
      <div className="h-1 w-full" />

      <div className="min-h-screen w-full px-4 sm:px-6 md:px-10 lg:px-16 py-10 sm:py-12">
        <div className="w-full max-w-2xl mx-auto">
          {/* Header nav */}
          <div className="mb-6 flex items-center justify-between">
            <button
              onClick={handleEditDetails}
              className="inline-flex items-center gap-2 rounded-full border border-[#E5E7EB] bg-white px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-[#6A548A] shadow-sm hover:bg-[#F7F2FB] transition"
            >
              ← Edit Details
            </button>
            <div className="flex items-center gap-2">
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: "#C084FC" }}
              />
              <span
                className="text-[10px] tracking-[0.2em] uppercase"
                style={{ color: "#9D8FA8" }}
              >
                Marriage Verdict
              </span>
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: "#C084FC" }}
              />
            </div>
            <div className="w-24" />
          </div>

          {/* ── HERO BANNER ── */}
          <motion.div
            custom={0}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="rounded-3xl p-6 sm:p-8 mb-5 text-center shadow-sm border"
            style={{
              background:
                "linear-gradient(135deg, #F7F2FB 0%, #F7F2FB 50%, #EFF6FF 100%)",
              borderColor: "#EEF2FF",
            }}
          >
            {/* Partner names + birth details */}
            <div className="flex items-start justify-center gap-3 mb-5">
              <div className="flex flex-col items-center">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold text-sm shadow-md"
                  style={{
                    background: "linear-gradient(135deg, #C084FC, #6D4C92)",
                  }}
                >
                  {partnerAInfo?.full_name?.[0] || "A"}
                </div>
                <p
                  className="text-[10px] mt-1 font-medium"
                  style={{ color: "#6A548A" }}
                >
                  {partnerAInfo?.full_name || "Partner A"}
                </p>
                {partnerAInfo && (
                  <p
                    className="text-[9px] mt-0.5 text-center max-w-[110px]"
                    style={{ color: "#9D8FA8" }}
                  >
                    {[
                      partnerAInfo.date_of_birth,
                      partnerAInfo.time_of_birth,
                      partnerAInfo.place_of_birth,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                )}
              </div>
              <div className="flex flex-col items-center gap-1 pt-3">
                <span className="text-lg" style={{ color: "#C084FC" }}>
                  ∞
                </span>
              </div>
              <div className="flex flex-col items-center">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold text-sm shadow-md"
                  style={{
                    background: "linear-gradient(135deg, #C084FC, #6D4C92)",
                  }}
                >
                  {partnerBInfo?.full_name?.[0] || "B"}
                </div>
                <p
                  className="text-[10px] mt-1 font-medium"
                  style={{ color: "#6D4C92" }}
                >
                  {partnerBInfo?.full_name || "Partner B"}
                </p>
                {partnerBInfo && (
                  <p
                    className="text-[9px] mt-0.5 text-center max-w-[110px]"
                    style={{ color: "#9D8FA8" }}
                  >
                    {[
                      partnerBInfo.date_of_birth,
                      partnerBInfo.time_of_birth,
                      partnerBInfo.place_of_birth,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                )}
              </div>
            </div>

            {/* Score ring */}
            <ScoreGauge
              score={astro.compatibility.guna_score_percent}
              label="Cosmic Match"
              size={96}
              strokeColor="#C084FC"
            />

            <div className="flex flex-wrap justify-center gap-2 mt-4">
              <TagBadge
                text={`Guna Milan: ${astro.compatibility.guna_label}`}
                color="purple"
              />
              <TagBadge
                text={`${astro.compatibility.guna_score}/${astro.compatibility.guna_score_max}`}
                color="blue"
              />
              <TagBadge
                text={`Manglik: ${astro.compatibility.manglik_status}`}
                color="amber"
              />
            </div>
          </motion.div>

          {/* ── TAB TOGGLE ── */}
          <TabToggle active={activeTab} onChange={handleTabChange} />

          {/* ═══ TAB 1: LIFE GUIDANCE ═══ */}
          {activeTab === "life_guidance" && (
            <>
              <SectionCard title="Healing" accent="#C084FC" index={1}>
                <p className="text-xs leading-relaxed" style={{ color: "#5D544D" }}>
                  {lg.healing}
                </p>
              </SectionCard>

              <SectionCard title="Growth Path" accent="#C084FC" index={2}>
                <p className="text-xs leading-relaxed" style={{ color: "#5D544D" }}>
                  {lg.growth_path}
                </p>
              </SectionCard>

              <SectionCard
                title="Family & Cultural Harmony"
                accent="#C084FC"
                index={3}
              >
                <p className="text-xs leading-relaxed" style={{ color: "#5D544D" }}>
                  {lg.family_cultural_harmony}
                </p>
              </SectionCard>

              <SectionCard
                title="Wedding Guidance"
                subtitle={lg.wedding_guidance.note}
                accent="#6D4C92"
                index={4}
              >
                <div
                  className="rounded-2xl p-4 mb-4 border"
                  style={{
                    background: "linear-gradient(90deg,#F7F2FB,#F7F2FB)",
                    borderColor: "#EEF2FF",
                  }}
                >
                  <p
                    className="text-[10px] font-semibold tracking-[0.1em] uppercase mb-1"
                    style={{ color: "#C084FC" }}
                  >
                    Vastu-Aligned Direction
                  </p>
                  <p
                    className="text-xs leading-relaxed"
                    style={{ color: "#5D544D" }}
                  >
                    {lg.wedding_guidance.vastu_direction}
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <p
                      className="text-[10px] font-semibold tracking-[0.15em] uppercase mb-2"
                      style={{ color: "#22C55E" }}
                    >
                      Lucky Colors
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {lg.wedding_guidance.lucky_colors.map((c) => (
                        <ColorChip key={c} color={c} />
                      ))}
                    </div>
                  </div>
                  {lg.wedding_guidance.colors_to_avoid.length > 0 && (
                    <div>
                      <p
                        className="text-[10px] font-semibold tracking-[0.15em] uppercase mb-2"
                        style={{ color: "#9D8FA8" }}
                      >
                        Avoid
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {lg.wedding_guidance.colors_to_avoid.map((c) => (
                          <ColorChip key={c} color={c} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {lg.wedding_guidance.recommended_gifts.length > 0 && (
                  <div className="mb-3">
                    <p
                      className="text-[10px] font-semibold tracking-[0.15em] uppercase mb-2"
                      style={{ color: "#22C55E" }}
                    >
                      Recommended Gifts
                    </p>
                    <BulletList
                      items={lg.wedding_guidance.recommended_gifts}
                      accent="#22C55E"
                    />
                  </div>
                )}
                {lg.wedding_guidance.checklist_highlights.length > 0 && (
                  <div>
                    <p
                      className="text-[10px] font-semibold tracking-[0.15em] uppercase mb-2"
                      style={{ color: "#C084FC" }}
                    >
                      Checklist Highlights
                    </p>
                    <BulletList
                      items={lg.wedding_guidance.checklist_highlights}
                      accent="#C084FC"
                    />
                  </div>
                )}
              </SectionCard>
            </>
          )}

          {/* ═══ TAB 2: ASTROLOGICAL CHART VIEW ═══ */}
          {activeTab === "astro_chart_view" && (
            <>
              {astro.is_limited && <LimitedBanner />}

              <SectionCard
                title="Core Chart"
                subtitle="Your foundational placements."
                accent="#C084FC"
                index={1}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[astro.partner_a, astro.partner_b].map((p, i) => (
                    <div
                      key={i}
                      className="rounded-2xl p-3 border"
                      style={{ background: "#F7F2FB", borderColor: "#EEF2FF" }}
                    >
                      <p
                        className="text-[10px] font-semibold mb-2"
                        style={{ color: "#6A548A" }}
                      >
                        {p.name}
                      </p>
                      {p.is_limited ? (
                        <p className="text-[10px]" style={{ color: "#9D8FA8" }}>
                          {p.place_provided && !p.place_resolved
                            ? "We couldn't precisely locate that birth place — try a nearby major city."
                            : "Add time & place for full chart."}
                        </p>
                      ) : (
                        <>
                          <p className="text-[10px]" style={{ color: "#9D8FA8" }}>
                            Lagna:{" "}
                            <span style={{ color: "#5D544D" }}>{p.lagna}</span>
                          </p>
                          <p className="text-[10px]" style={{ color: "#9D8FA8" }}>
                            Moon Sign:{" "}
                            <span style={{ color: "#5D544D" }}>
                              {p.moon_sign}
                            </span>
                          </p>
                          <p className="text-[10px]" style={{ color: "#9D8FA8" }}>
                            Nakshatra:{" "}
                            <span style={{ color: "#5D544D" }}>
                              {p.nakshatra}
                            </span>
                          </p>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </SectionCard>

              <SectionCard title="Compatibility Factors" accent="#6D4C92" index={2}>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <ScoreGauge
                    score={astro.compatibility.guna_score}
                    max={astro.compatibility.guna_score_max}
                    label="Guna Milan"
                    size={72}
                    strokeColor="#C084FC"
                  />
                  <div
                    className="flex flex-col items-center justify-center rounded-2xl p-2"
                    style={{ background: "#F7F2FB" }}
                  >
                    <p
                      className="text-base font-light text-center"
                      style={{ color: "#6D4C92" }}
                    >
                      {astro.compatibility.manglik_status}
                    </p>
                    <p
                      className="text-[10px] text-center mt-1"
                      style={{ color: "#9D8FA8" }}
                    >
                      Manglik Status
                    </p>
                  </div>
                  <div
                    className="flex flex-col items-center justify-center rounded-2xl p-2"
                    style={{ background: "#F7F2FB" }}
                  >
                    <p
                      className="text-[11px] font-light text-center"
                      style={{ color: "#6D4C92" }}
                    >
                      {astro.compatibility.rashi_relationship || "—"}
                    </p>
                    <p
                      className="text-[10px] text-center mt-1"
                      style={{ color: "#9D8FA8" }}
                    >
                      Rashi Relationship
                    </p>
                  </div>
                </div>
                <p
                  className="text-[10px] font-semibold tracking-[0.15em] uppercase mb-2"
                  style={{ color: "#9D8FA8" }}
                >
                  Dosha
                </p>
                <div className="flex flex-wrap gap-2">
                  {astro.compatibility.dosha.length > 0 ? (
                    astro.compatibility.dosha.map((d) => (
                      <TagBadge key={d} text={d} color="rose" />
                    ))
                  ) : (
                    <TagBadge text="None" color="green" />
                  )}
                </div>
              </SectionCard>

              <SectionCard title="Planetary Highlights" accent="#C084FC" index={3}>
                <BulletList items={astro.planetary_highlights} />
              </SectionCard>

              <SectionCard title="House Influences" accent="#C084FC" index={4}>
                <BulletList items={astro.house_influences} />
              </SectionCard>

              <SectionCard title="Dasha Summary" accent="#6D4C92" index={5}>
                <p className="text-xs leading-relaxed" style={{ color: "#5D544D" }}>
                  {astro.dasha_summary}
                </p>
              </SectionCard>

              <SectionCard title="Three-Point Summary" accent="#C084FC" index={6}>
                <BulletList items={astro.summary_points} />
              </SectionCard>
            </>
          )}

          {/* Footer actions */}
          <div className="flex flex-wrap justify-center gap-3 mt-6 pb-4">
            <button
              onClick={handleEditDetails}
              className="py-3 px-6 rounded-full border border-[#E5E7EB] bg-white text-xs font-semibold uppercase tracking-[0.15em] text-[#6A548A] shadow-sm transition hover:bg-[#F7F2FB]"
            >
              Edit Details
            </button>
            <button
              onClick={handleSaveVerdict}
              className="py-3 px-6 rounded-full border text-xs font-semibold uppercase tracking-[0.15em] shadow-sm transition"
              style={
                saved
                  ? { background: "#F0FDF4", borderColor: "#86EFAC", color: "#166534" }
                  : { background: "white", borderColor: "#E5E7EB", color: "#6A548A" }
              }
            >
              {saved ? "Saved ✓" : "Save Verdict"}
            </button>
            <button
              onClick={handleNewVerdict}
              className="py-3 px-6 rounded-full bg-gradient-to-r from-[#C084FC] to-[#7DD3FC] text-xs font-semibold uppercase tracking-[0.15em] text-white shadow-lg transition-all duration-200 hover:opacity-90"
            >
              ✨ New Verdict
            </button>
          </div>

          <div
            className="text-center mt-2 pb-8 text-[10px] tracking-wide"
            style={{ color: "#9D8FA8" }}
          >
            <span
              className="inline-block border-t w-12 mx-2 align-middle"
              style={{ borderColor: "#C084FC40" }}
            />
            Astria aligns your path, not dictates it.
            <span
              className="inline-block border-t w-12 mx-2 align-middle"
              style={{ borderColor: "#C084FC40" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarriageVerdictResult;
